import { useState, useEffect } from "react";
import { SEO } from "@/components/SEO";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Award, Users, TrendingUp, Shield, Target, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface StatItem {
  icon: string;
  value: string;
  label: string;
}

interface ValueItem {
  icon: string;
  title: string;
  description: string;
}

interface TimelineItem {
  year: string;
  title: string;
  description: string;
}

const About = () => {
  const [loading, setLoading] = useState(true);
  
  // Content states
  const [headerTitle, setHeaderTitle] = useState("About Ascot Wealth Management");
  const [headerDescription, setHeaderDescription] = useState("We are a leading UK investment management firm dedicated to helping individuals and families achieve their financial goals through professional portfolio management.");
  const [storyTitle, setStoryTitle] = useState("Our Story");
  const [storyParagraphs, setStoryParagraphs] = useState<string[]>([
    "Founded in 2009, Ascot Wealth Management Ltd. (trading as Cape Berkshire Asset Management) was born from a simple belief: professional investment management should be accessible to all UK investors, not just the ultra-wealthy.",
    "Our founders, with decades of combined experience at leading investment banks and wealth management firms, recognized that individual investors deserved the same level of professional portfolio management as institutional clients.",
    "Today, we manage over £250 million for more than 500 clients across the UK, maintaining our commitment to transparency, performance, and personalized service that has defined us from the beginning."
  ]);
  const [valuesTitle, setValuesTitle] = useState("Our Values");
  const [valuesDescription, setValuesDescription] = useState("The principles that guide every decision we make and every relationship we build.");
  const [timelineTitle, setTimelineTitle] = useState("Our Journey");
  const [timelineDescription, setTimelineDescription] = useState("Key milestones in our growth and development as a leading UK investment firm.");
  const [regulatoryTitle, setRegulatoryTitle] = useState("Regulatory & Compliance");
  const [regulatoryDescription, setRegulatoryDescription] = useState("Your investments are protected by the highest regulatory standards.");
  const [ctaTitle, setCtaTitle] = useState("Ready to Start Your Investment Journey?");
  const [ctaDescription, setCtaDescription] = useState("Join hundreds of satisfied clients who trust us with their financial future. Book a consultation to learn how we can help you achieve your goals.");

  // Dynamic content states
  const [stats, setStats] = useState<StatItem[]>([
    { icon: "Users", value: "500+", label: "Satisfied Clients" },
    { icon: "TrendingUp", value: "£250M+", label: "Assets Under Management" },
    { icon: "Award", value: "15+", label: "Years Experience" },
    { icon: "Shield", value: "100%", label: "FCA Regulated" }
  ]);
  
  const [values, setValues] = useState<ValueItem[]>([
    { icon: "Target", title: "Client-Focused", description: "Every decision we make is guided by what's best for our clients' long-term financial success." },
    { icon: "Shield", title: "Transparent", description: "We believe in complete transparency in our fees, processes, and investment approach." },
    { icon: "TrendingUp", title: "Performance-Driven", description: "Our focus is on delivering consistent, risk-adjusted returns that meet your objectives." },
    { icon: "Users", title: "Partnership Approach", description: "We build long-term relationships based on trust, communication, and mutual respect." }
  ]);

  const [timeline, setTimeline] = useState<TimelineItem[]>([
    { year: "2009", title: "Company Founded", description: "Ascot Wealth Management Ltd. (trading as Cape Berkshire Asset Management) was established with a vision to democratize professional investment management." },
    { year: "2012", title: "FCA Authorization", description: "Received full authorization from the Financial Conduct Authority, ensuring regulatory compliance." },
    { year: "2015", title: "£50M Milestone", description: "Reached £50 million in assets under management, serving over 200 satisfied clients." },
    { year: "2018", title: "Technology Innovation", description: "Launched our proprietary portfolio management platform for enhanced client service." },
    { year: "2020", title: "ESG Integration", description: "Introduced ESG-focused investment options across all our model portfolios." },
    { year: "2024", title: "Market Leader", description: "Now managing over £250M for 500+ clients with industry-leading performance." }
  ]);

  useEffect(() => {
    let isMounted = true;
    
    const fetchAboutContent = async () => {
      if (!isMounted) return;
      setLoading(true);
      
      try {
        const { data: contentData, error } = await supabase
          .from('service_page_settings')
          .select('*')
          .like('key', 'about_%');

        if (error && !error.message.includes('does not exist')) {
          console.error('Error fetching about content:', error);
          if (isMounted) {
            setLoading(false);
          }
          return;
        }

        if (contentData && contentData.length > 0 && isMounted) {
          contentData.forEach(item => {
            const value = item.value;
            try {
              switch (item.key) {
                case 'about_header_title':
                  setHeaderTitle(value);
                  break;
                case 'about_header_description':
                  setHeaderDescription(value);
                  break;
                case 'about_story_title':
                  setStoryTitle(value);
                  break;
                case 'about_story_paragraphs':
                  setStoryParagraphs(JSON.parse(value));
                  break;
                case 'about_values_title':
                  setValuesTitle(value);
                  break;
                case 'about_values_description':
                  setValuesDescription(value);
                  break;
                case 'about_timeline_title':
                  setTimelineTitle(value);
                  break;
                case 'about_timeline_description':
                  setTimelineDescription(value);
                  break;
                case 'about_regulatory_title':
                  setRegulatoryTitle(value);
                  break;
                case 'about_regulatory_description':
                  setRegulatoryDescription(value);
                  break;
                case 'about_cta_title':
                  setCtaTitle(value);
                  break;
                case 'about_cta_description':
                  setCtaDescription(value);
                  break;
                case 'about_stats':
                  setStats(JSON.parse(value));
                  break;
                case 'about_values':
                  setValues(JSON.parse(value));
                  break;
                case 'about_timeline':
                  setTimeline(JSON.parse(value));
                  break;
              }
            } catch (parseError) {
              console.error(`Error parsing ${item.key}:`, parseError);
            }
          });
        }
      } catch (error) {
        console.error('Error fetching about content:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    fetchAboutContent();
    
    return () => {
      isMounted = false;
    };
  }, []);

  const getIcon = (iconName: string) => {
    const icons: Record<string, any> = {
      Users,
      TrendingUp,
      Award,
      Shield,
      Target,
      Clock
    };
    return icons[iconName] || Users;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="py-8">
      <SEO
        title="About Cape Berkshire Asset Management"
        description="Learn about CBAM, an FCA-regulated discretionary investment manager providing model portfolio services and bespoke wealth solutions to UK financial advisers."
        path="/about"
      />
      {/* Header */}
      <section className="bg-gradient-to-br from-primary to-accent text-primary-foreground py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">{headerTitle}</h1>
            <p className="text-xl text-primary-foreground/90 max-w-3xl mx-auto">
              {headerDescription}
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-background">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const IconComponent = getIcon(stat.icon);
              return (
                <Card key={index} className="text-center border-0 shadow-lg">
                  <CardHeader>
                    <div className="mx-auto w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                      <IconComponent className="h-8 w-8 text-primary" />
                    </div>
                    <CardTitle className="text-3xl font-bold text-primary">{stat.value}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base font-medium">{stat.label}</CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 bg-muted/50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">{storyTitle}</h2>
              <div className="space-y-6 text-lg">
                {storyParagraphs.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square bg-gradient-to-br from-primary/10 to-accent/10 rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <div className="w-24 h-24 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Clock className="h-12 w-12 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">15+ Years</h3>
                  <p className="text-muted-foreground">of Investment Excellence</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 bg-background">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{valuesTitle}</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {valuesDescription}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const IconComponent = getIcon(value.icon);
              return (
                <Card key={index} className="text-center border-0 shadow-lg">
                  <CardHeader>
                    <div className="mx-auto w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                      <IconComponent className="h-8 w-8 text-primary" />
                    </div>
                    <CardTitle className="text-xl">{value.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base">{value.description}</CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-16 bg-muted/50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{timelineTitle}</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {timelineDescription}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {timeline.map((item, index) => (
              <Card key={index} className="relative">
                <CardHeader>
                  <Badge className="w-fit mb-2">{item.year}</Badge>
                  <CardTitle className="text-lg">{item.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">{item.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Regulatory Information */}
      <section className="py-16 bg-background">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{regulatoryTitle}</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {regulatoryDescription}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="text-center border-primary/20">
              <CardHeader>
                <div className="mx-auto w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="text-xl">FCA Regulated</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Fully authorized and regulated by the Financial Conduct Authority 
                  (Firm Reference Number: 551744).
                </CardDescription>
              </CardContent>
            </Card>
            <Card className="text-center border-primary/20">
              <CardHeader>
                <div className="mx-auto w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Award className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="text-xl">FSCS Protected</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Your investments are protected up to £85,000 per person under the 
                  Financial Services Compensation Scheme.
                </CardDescription>
              </CardContent>
            </Card>
            <Card className="text-center border-primary/20">
              <CardHeader>
                <div className="mx-auto w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Users className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="text-xl">Professional Indemnity</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  We maintain comprehensive professional indemnity insurance to 
                  protect our clients' interests.
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary text-primary-foreground">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            {ctaTitle}
          </h2>
          <p className="text-xl mb-8 text-primary-foreground/90 max-w-2xl mx-auto">
            {ctaDescription}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" variant="secondary" asChild>
              <Link to="/contact">Book Consultation</Link>
            </Button>
            <Button size="lg" variant="secondary" className="bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground hover:text-primary" asChild>
              <Link to="/team">Meet Our Team</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;